import React, { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

import Paths from 'commons/paths';
import Header from 'components/organisms/Header';
import Sider from 'components/organisms/Sider';
import Footer from 'components/organisms/Footer';
import classNames from 'classnames';
import MainVideoPlayer from 'components/organisms/MainVideoPlayer';
import { useCheckAuthTokens, useGAObject, useUserRank, useVideo } from 'hooks';
import { AppError, Error404, Error504 } from 'components/pages';
import { ALLOWED_COOKIES_DOMAIN_TYPE_LIST } from '../../commons/constants';
import { isIEBrowser, mobileCheck } from '../../utils/utils';
import WarningPopup from '../organisms/Popup/WarningPopup';
import moment from 'moment';
import useMyInfo from '../../hooks/useMyInfo';
import OnboardingPopup from '../organisms/Popup/OnboardingPoup';
import usePopupInfo from '../../hooks/usePopupInfo';
import { ErrorSystem, ErrorSystemEP } from '../pages';
import paths from 'commons/paths';
import TextPopup from "../organisms/Popup/TextPopup";
import ImagePopup from "../organisms/Popup/ImagePopup";

export interface DefaultLayoutProps {}

const NORMAL_POPUP_HIDE_DAY_TERMS = 1;
const RESULT_CODE_POPUP_INFO_NOT_EXIST = 1102;

/** 정기 PM 작업 에러 페이지 노출 시작일 */
const SHOW_PM_ERROR_PAGE_DATE = new Date('2025.11.15 23:00:00');
/** 정기 PM 작업 에러 페이지 노출 종료일 */
const HIDE_PM_ERROR_PAGE_DATE = new Date('2025.11.16 06:00:00');
/** 정기 PM 작업 시작일 - 페이지 표기 시작일 */
const PM_SERVER_ERROR_CHECKED_DATE_START = new Date('2025.11.15 23:00:00');
/** 정기 PM 작업 종료일 - 페이지 표기 종료일 */
const PM_SERVER_ERROR_CHECKED_DATE_END = new Date('2025.11.16 06:00:00');

const DefaultLayout = ({ children }: React.PropsWithChildren<DefaultLayoutProps>): React.ReactElement => {
  const { data } = useVideo();
  const location = useLocation();
  const { checkLogin } = useCheckAuthTokens();
  const { getOnboardingPopupInfo, getNormalPopupInfo, readNormalPopup } = usePopupInfo();
  const { info } = useMyInfo();

  /** IE 경고 팝업 열기/닫기 */
  const [isOpenWarningPopup, setIsOpenWarningPopup] = useState(false);
  /** 온보딩 팝업 노출 여부 */
  const [showOnboardingPopup, setShowOnboardingPopup] = useState<boolean>(false);
  /** 온보딩 팝업 상세 정보 */
  const [onboardingPopupData, setOnboardingPopupData] = useState<PopupInfoDetailResDto>();
  /** 일반 팝업 상세 정보 */
  const [normalPopupData, setNormalPopupData] = useState<PopupInfoDetailResDto>();
  /** 일반 팝업 노출 여부 */
  const [showNormalPopup, setShowNormalPopup] = useState<boolean>(false);
  /** 온보딩 팝업 조회 완료 여부 */
  const [isOnboardingPopupChecked, setIsOnboardingPopupChecked] = useState<boolean>(false);

  const isShowIEWarningPopup = useMemo(() => {
    if (!isIEBrowser()) return false;
    if (
      !localStorage.getItem('hide-warning-popup-expired-date') ||
      !moment(localStorage.getItem('hide-warning-popup-expired-date'), 'YYYY.MM.DD').isValid()
    )
      return true;
    const hideExpiredDate = moment(localStorage.getItem('hide-warning-popup-expired-date'), 'YYYY.MM.DD');
    return moment(new Date(), 'YYYY.MM.DD').isSameOrAfter(hideExpiredDate);
  }, [location.pathname]);

  /** PM 에러 페이지 노출 여부 */
  const hasPMServerCheckPeriod = useMemo(() => {
    const nowDate = moment(new Date());
    const pmStartDt = moment(SHOW_PM_ERROR_PAGE_DATE);
    const pmEndDt = moment(HIDE_PM_ERROR_PAGE_DATE);
    if (nowDate.isBefore(pmStartDt)) return false;
    if (nowDate.isAfter(pmEndDt)) return false;
    return nowDate.isSameOrAfter(pmStartDt) && nowDate.isSameOrBefore(pmEndDt);
  }, [location.pathname]);

  // 임시 시스템 점검 에러 페이지 (정기 PM 작업)
  if (hasPMServerCheckPeriod) {
    if (Paths.getPageName(location.pathname) === 'EP Panel') {
      return (
        <ErrorSystemEP
          startErrorDate={PM_SERVER_ERROR_CHECKED_DATE_START}
          endErrorDate={PM_SERVER_ERROR_CHECKED_DATE_END}
        />
      );
    }
    // 정기 PM 작업 EP 패널만 점검 페이지로 이동
    return (
      <ErrorSystem
        startErrorDate={PM_SERVER_ERROR_CHECKED_DATE_START}
        endErrorDate={PM_SERVER_ERROR_CHECKED_DATE_END}
      />
    );
  }

  //
  useGAObject();
  useUserRank();

  // mobilelife.lg.com 으로 접속 시  모바일 App이 아닌 경우 오류 페이지로 이동
  if (new URL(window.location.href).hostname === 'mobilelife.lg.com') {
    const lifeAuth = Cookies.get('lifeAuth');

    if (ALLOWED_COOKIES_DOMAIN_TYPE_LIST.every(item => item.code !== lifeAuth) && lifeAuth !== 'EMS') {
      return <AppError />;
    }
  }

  const noLayout =
    !!matchPath(location.pathname, paths.getPath('user')) ||
    !!matchPath(location.pathname, paths.getPath('ep')) ||
    !!matchPath(location.pathname, paths.getPath('mobileCheck')) ||
    !!matchPath(location.pathname, paths.getPath('autoLoginCheck')) ||
    !!matchPath(location.pathname, paths.getPath('withdrawComplete'));

  const contentsRef = useRef<HTMLDivElement>(null);

  /**
   * 상세 페이지 여부
   */
  const isDetailPage = useMemo(() => {
    return (
      location.pathname.includes('/detail') ||
      location.pathname.includes('/anniversary75') ||
      location.pathname.includes('/event/campaign_1')
    );
  }, [location.pathname]);

  /**
   * 커뮤니티 게시판 작성 페이지 여부
   */
  const isCommunityWritePage = useMemo(() => {
    return (
      location.pathname === Paths.getPath('boardWrite') ||
      location.pathname === Paths.getPath('topEatsBoardWrite') ||
      location.pathname === Paths.getPath('mentionsBoardWrite') ||
      location.pathname === Paths.getPath('departmentWrite')
    );
  }, [location.pathname]);

  /**
   * 동영상 메뉴 상세페이지 여부
   */
  const isVideoDetailPage = useMemo(() => {
    return location.pathname.includes(Paths.getPath('videoDetail').replace('/:mediaId', ''));
  }, [location.pathname]);

  /**
   * 숏폼 페이지 여부
   */
  const isShortsPage = useMemo(() => {
    return location.pathname.includes('shorts-media');
  }, [location.pathname]);

  if (Paths.getPageName(location.pathname) === '404') {
    return <Error404 />;
  }

  // 504 에러 페이지 처리 추가
  if (Paths.getPageName(location.pathname) === '504') {
    return <Error504 />;
  }


  if (!noLayout) {
    checkLogin();
  }

  const handleResize = () => {
    if (window.innerWidth < 1024) document.querySelector('body')?.classList.add('mobile');
    else document.querySelector('body')?.classList.remove('mobile');
  };

  /**
   * 팝업 열람 처리와 함께 일반 팝업 닫기
   */
  const onCloseNormalPopupWithReadPopup = async () => {
    if (!normalPopupData) return;

    try {
      const res = await readNormalPopup({
        popupId: normalPopupData.popupId,
        expiredDayTerms: NORMAL_POPUP_HIDE_DAY_TERMS,
      });
      if (res.resultCode !== 200) {
        console.error(res.resultMessage);
      }
    } catch (error) {
      console.error(error);
    } finally {
      // 열람 처리 성공 여부와 무관하게 팝업은 닫는다
      setShowNormalPopup(false);
    }
  };

  useEffect(() => {
    setIsOpenWarningPopup(isShowIEWarningPopup);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (mobileCheck()) {
      document.querySelector('body')?.classList.add('real-mobile');
      return;
    }

    document.querySelector('body')?.classList.remove('real-mobile');
  }, [location.pathname]);

  useEffect(() => {
    if (info) {
      getOnboardingPopupInfo()
        .then(res => {
          if (res.resultCode !== 200) {
            console.error(res.resultMessage);
            return;
          }
          setOnboardingPopupData(res);
          setShowOnboardingPopup(true);
        })
        .catch(error => console.error(error))
        .finally(() => setIsOnboardingPopupChecked(true));

      getNormalPopupInfo()
        .then(res => {
          if (res.resultCode === RESULT_CODE_POPUP_INFO_NOT_EXIST) return;
          if (res.resultCode !== 200) {
            console.error(res.resultMessage);
            return;
          }

          if (!res.popupId || res.openYn !== 'Y') return;

          setNormalPopupData(res);
          setShowNormalPopup(true);
        })
        .catch(error => console.error(error));
    }
  }, [info]);

  const isSearchPage = useMemo(() => /^\/search/gi.test(location.pathname), [location.pathname]);
  const isWithdrawCompletePage = useMemo(() => location.pathname.includes(paths.getPath('withdrawComplete')), [location.pathname]);
  const isWithdrawConfirmPage = useMemo(() => location.pathname.includes(paths.getPath('withdrawConfirm')), [location.pathname]);

  const isEpPage = useMemo(() => !!matchPath(location.pathname, paths.getPath('ep')), [location.pathname]);
  const hasOnboardingPopup = !isEpPage && !!onboardingPopupData?.imageUri;

  const isNormalPopupVisible = useMemo(() => {
    return (
      location.pathname === '/'
      // 추가 조건
    );
  }, [location.pathname]);

  const isNormalPopupOpen =
    showNormalPopup && isNormalPopupVisible && isOnboardingPopupChecked && !hasOnboardingPopup;

  useEffect(() => {
    document.body.style.overflow = isNormalPopupOpen || (showOnboardingPopup && hasOnboardingPopup) ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isNormalPopupOpen, showOnboardingPopup, hasOnboardingPopup]);

  const Content = useMemo(() => <>{children}</>, []);

  return (
    <>
      <div className={classNames(['wrapper', { search: isSearchPage }, {secession_complete: isWithdrawCompletePage }])}>
        {noLayout ? (
          Content
        ) : (
          <>
            <Header />
            <div
              className={classNames('container', {
                detail: isDetailPage || isCommunityWritePage || isWithdrawConfirmPage,
                'pip-mode': data.pip,
                article:
                  (isDetailPage && !isVideoDetailPage && (!data.visible || (data.pip && data.visible))) ||
                  isCommunityWritePage || isWithdrawConfirmPage,
                list:
                  location.pathname.includes('community') &&
                  !isCommunityWritePage
                  // 고객 요청으로 인해 주석 처리(추후 재노출 가능성이 존재하여 화면에서만 미노출 처리) - 2025.03.10
                  // && location.pathname !== Paths.getPath('eventSimpleSurveyList'),
              })}
            >
              {/* <div className={classNames('container', { detail: isDetailPage, 'pip-mode': data.pip })}> */}
              <Sider />
              <div className={classNames('content', { shorts: isShortsPage })} id="content" ref={contentsRef}>
                {Content}
              </div>
              <MainVideoPlayer />
            </div>
            <Footer></Footer>
          </>
        )}
      </div>
      <WarningPopup
        isOpen={isOpenWarningPopup}
        popUpContents={
          '' +
          `※크롬/엣지 브라우저를 사용해주세요※<br>
          지금 사용하고 계신 브라우저는 LG LIFE<br>
          이용 시 불편이 있을 수 있습니다.<br>
          원활한 서비스 이용을 위해<br>
          크롬, 엣지 브라우저를 사용해주세요 :)<br>` +
          ''
        }
        registerHidePopUpOptions={{
          contents: '일주일간 보지 않기',
          term: 7,
        }}
        onClose={() => setIsOpenWarningPopup(false)}
      />
      <OnboardingPopup
        isOpen={showOnboardingPopup}
        linkUrl={onboardingPopupData?.linkUri as string}
        popupImageUri={onboardingPopupData?.imageUri as string}
        onClose={() => setShowOnboardingPopup(false)}
      />

      {isNormalPopupOpen && normalPopupData && (
        <>
          {normalPopupData.contentType === 'TEXT' && (
            <TextPopup
              popupData={normalPopupData}
              onCloseWithReadPopup={onCloseNormalPopupWithReadPopup}
              onClose={() => setShowNormalPopup(false)}
            />
          )}
          {normalPopupData.contentType === 'IMAGE' && (
            <ImagePopup
              popupData={normalPopupData}
              onCloseWithReadPopup={onCloseNormalPopupWithReadPopup}
              onClose={() => setShowNormalPopup(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default DefaultLayout;
